import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTheme } from "@mui/material";
import "../pages/Home/Live/LiveUnit.css";

const TRANSCRIPT_BASE_URL = "/transcript-api"; // Adjust this to your actual transcript service base URL

interface Meeting {
  id: string;
  name?: string;
  title?: string;
  date?: string;
  start_time?: string;
  duration?: number;
}

interface MeetingDetail {
  id: string;
  summary?: string;
  transcript?: string;
  utterances?: TranscriptUtterance[];
  title?: string;
  date?: string;
  start_time?: string;
  duration?: number;
}

interface TranscriptUtterance {
  id: number;
  start: number;
  end: number;
  text: string;
  speaker?: string;
}

interface Props {
  deviceId: string;
  accessToken?: string;
}

const formatDateTime = (value?: string | number) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleString();
};

const formatDuration = (duration?: number) => {
  if (duration == null) return "—";
  if (duration < 1) return `${Math.round(duration * 60)} sec`;
  return `${Math.round(duration * 10) / 10} min`;
};

const formatTranscriptTime = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    : `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const parseTableRow = (line: string) => {
  const trimmedLine = line.trim();
  if (!trimmedLine.includes("|")) return null;

  const cells = trimmedLine.startsWith("|")
    ? trimmedLine.slice(1)
    : trimmedLine;
  const normalizedCells = cells.endsWith("|") ? cells.slice(0, -1) : cells;
  return normalizedCells.split("|").map((cell) => cell.trim());
};

const isTableSeparator = (line: string) => {
  const cells = parseTableRow(line);
  return (
    Boolean(cells?.length) && cells!.every((cell) => /^:?-{3,}:?$/.test(cell))
  );
};

const renderSummary = (summary: string): React.ReactNode[] => {
  const lines = summary.split(/\r?\n/);
  const rendered: React.ReactNode[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmedLine = line.trim();
    const key = `${index}-${trimmedLine}`;
    let separatorIndex = index + 1;
    while (separatorIndex < lines.length && !lines[separatorIndex].trim()) {
      separatorIndex += 1;
    }

    if (parseTableRow(line) && isTableSeparator(lines[separatorIndex] ?? "")) {
      const header = parseTableRow(line)!;
      const rows: string[][] = [];
      let nextIndex = separatorIndex + 1;

      while (nextIndex < lines.length) {
        if (!lines[nextIndex].trim()) {
          nextIndex += 1;
          continue;
        }

        const row = parseTableRow(lines[nextIndex]);
        if (!row) break;
        rows.push(row);
        nextIndex += 1;
      }

      rendered.push(
        <TableContainer key={key} sx={{ my: 1, overflowX: "auto" }}>
          <Table size="small" aria-label="summary table">
            <TableHead>
              <TableRow>
                {header.map((cell, cellIndex) => (
                  <TableCell
                    key={`${key}-header-${cellIndex}`}
                    sx={{ fontWeight: 600 }}
                  >
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow key={`${key}-row-${rowIndex}`}>
                  {header.map((_, cellIndex) => (
                    <TableCell key={`${key}-${rowIndex}-${cellIndex}`}>
                      {row[cellIndex] ?? ""}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>,
      );
      index = nextIndex - 1;
      continue;
    }

    if (!trimmedLine) {
      rendered.push(<Box key={key} sx={{ height: 0.75 }} />);
      continue;
    }

    const heading = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      rendered.push(
        <Typography
          key={key}
          variant={heading[1].length === 1 ? "subtitle1" : "subtitle2"}
          sx={{ mt: heading[1].length === 1 ? 1 : 1.5, fontWeight: 600 }}
        >
          {heading[2]}
        </Typography>,
      );
      continue;
    }

    const italic = trimmedLine.match(/^_(.+)_$/);
    if (italic) {
      rendered.push(
        <Typography key={key} variant="body2" color="text.secondary">
          {formatDateTime(italic[1])}
        </Typography>,
      );
      continue;
    }

    rendered.push(
      <Typography key={key} variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
        {line}
      </Typography>,
    );
  }

  return rendered;
};

const normalizeMeeting = (meeting: {
  id?: string;
  meetingId?: string;
  name?: string;
  title?: string;
  date?: string;
  start_time?: string;
  timestamp?: string;
  duration?: number;
  durationMs?: number;
}): Meeting => ({
  id: meeting.id ?? meeting.meetingId ?? "",
  name: meeting.name,
  title: meeting.title,
  date: meeting.date ?? meeting.timestamp,
  start_time: meeting.start_time ?? meeting.timestamp,
  duration:
    meeting.duration ??
    (meeting.durationMs != null ? meeting.durationMs / 60000 : undefined),
});

const normalizeMeetingDetail = (data: {
  id?: string;
  meetingId?: string;
  summary?: string;
  transcript?: string;
  title?: string;
  date?: string;
  start_time?: string;
  timestamp?: string;
  duration?: number;
  durationMs?: number;
}): MeetingDetail => ({
  id: data.id ?? data.meetingId ?? "",
  summary: data.summary,
  transcript: data.transcript,
  title: data.title,
  date: data.date ?? data.timestamp,
  start_time: data.start_time ?? data.timestamp,
  duration:
    data.duration ??
    (data.durationMs != null ? data.durationMs / 60000 : undefined),
});

const normalizeUtterances = (data: {
  data?: { utterances?: TranscriptUtterance[] };
  utterances?: TranscriptUtterance[];
}): TranscriptUtterance[] => data?.data?.utterances ?? data?.utterances ?? [];

export const TranscriptTab: React.FC<Props> = ({ deviceId, accessToken }) => {
  const theme = useTheme();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [detail, setDetail] = useState<MeetingDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setMeetings([]);

    const headers: Record<string, string> = {};
    if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

    console.debug("[TranscriptTab] Fetching meetings", { deviceId });

    fetch(
      `${TRANSCRIPT_BASE_URL}/meetings/name/${encodeURIComponent(deviceId.toLowerCase())}`,
      { headers },
    )
      .then((res) => {
        console.debug("[TranscriptTab] Meetings response", {
          deviceId,
          status: res.status,
        });
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          const rawMeetings = Array.isArray(data)
            ? data
            : (data?.data?.meetings ?? data?.meetings ?? []);
          const list = rawMeetings
            .map(normalizeMeeting)
            .filter((meeting: Meeting) => meeting.id);
          console.debug("[TranscriptTab] Meetings loaded", {
            deviceId,
            count: list.length,
            payloadIsArray: Array.isArray(data),
          });
          setMeetings(list);
        }
      })
      .catch((err) => {
        console.error("[TranscriptTab] Failed to fetch meetings", {
          deviceId,
          error: err,
        });
        if (!cancelled)
          setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [deviceId, accessToken]);

  const handleRowClick = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);

    const headers: Record<string, string> = {};
    if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

    console.debug("[TranscriptTab] Fetching meeting details", {
      meetingId: meeting.id,
    });

    Promise.all([
      fetch(
        `${TRANSCRIPT_BASE_URL}/meetings/id/${encodeURIComponent(meeting.id)}`,
        { headers },
      ),
      fetch(
        `${TRANSCRIPT_BASE_URL}/meetings/id/${encodeURIComponent(meeting.id)}/transcript/`,
        { headers },
      ),
    ])
      .then(async ([detailResponse, transcriptResponse]) => {
        console.debug("[TranscriptTab] Meeting details response", {
          meetingId: meeting.id,
          status: detailResponse.status,
          transcriptStatus: transcriptResponse.status,
        });
        if (!detailResponse.ok) {
          throw new Error(`Request failed: ${detailResponse.status}`);
        }
        if (!transcriptResponse.ok) {
          throw new Error(
            `Transcript request failed: ${transcriptResponse.status}`,
          );
        }

        const [detailData, transcriptData] = await Promise.all([
          detailResponse.json(),
          transcriptResponse.json(),
        ]);
        const normalizedDetail = normalizeMeetingDetail(
          detailData?.data ?? detailData,
        );
        return {
          ...normalizedDetail,
          utterances: normalizeUtterances(transcriptData),
        };
      })
      .then((normalizedDetail) => {
        console.debug("[TranscriptTab] Meeting details loaded", {
          meetingId: meeting.id,
          hasSummary: Boolean(normalizedDetail.summary),
          utteranceCount: normalizedDetail.utterances?.length ?? 0,
        });
        setDetail(normalizedDetail);
      })
      .catch((err) => {
        console.error("[TranscriptTab] Failed to fetch meeting details", {
          meetingId: meeting.id,
          error: err,
        });
        setDetailError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        setDetailLoading(false);
      });
  };

  const handleBack = () => {
    setSelectedMeeting(null);
    setDetail(null);
    setDetailError(null);
  };

  if (selectedMeeting) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            px: 1,
            pt: 0.5,
          }}
        >
          <IconButton size="small" onClick={handleBack}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2" sx={{ ml: 1 }}>
            {selectedMeeting.title ??
              selectedMeeting.name ??
              selectedMeeting.id}
          </Typography>
        </Box>
        <Box sx={{ flex: "1 1 auto", overflow: "auto", px: 1.5, py: 1 }}>
          {detailLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {detailError && (
            <Typography variant="body2" color="error">
              {detailError}
            </Typography>
          )}
          {detail && !detailLoading && (
            <Box>
              {detail.date && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  {formatDateTime(detail.start_time ?? detail.date)}
                  {detail.duration != null &&
                    ` · ${formatDuration(detail.duration)}`}
                </Typography>
              )}
              {detail.summary && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Summary
                  </Typography>
                  <Box>{renderSummary(detail.summary)}</Box>
                </>
              )}
              {(detail.transcript || detail.utterances?.length) && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1.5 }}>
                    Transcript
                  </Typography>
                  {detail.utterances?.length ? (
                    <Box>
                      {detail.utterances.map((utterance) => (
                        <Box key={utterance.id} sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatTranscriptTime(utterance.start)}
                            {utterance.speaker && ` · ${utterance.speaker}`}
                          </Typography>
                          <Typography variant="body2">
                            {utterance.text}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                      }}
                    >
                      {detail.transcript}
                    </Typography>
                  )}
                </>
              )}
              {!detail.summary &&
                !detail.transcript &&
                !detail.utterances?.length && (
                  <Typography variant="body2" color="text.secondary">
                    No details available.
                  </Typography>
                )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Table aria-label="transcripts header" sx={{ flex: "0 0 auto" }}>
        <TableHead className="monitorHeader">
          <TableRow>
            <TableCell
              style={{ width: "40%", color: theme.palette.common.white }}
            >
              Date
            </TableCell>
            <TableCell
              style={{ color: theme.palette.common.white }}
              align="left"
            >
              Meeting
            </TableCell>
            <TableCell
              style={{ width: "20%", color: theme.palette.common.white }}
              align="right"
            >
              Duration
            </TableCell>
          </TableRow>
        </TableHead>
      </Table>
      <TableContainer
        sx={{ flex: "1 1 auto", overflow: "auto", minHeight: 100 }}
      >
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && (
          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}
        {!loading && !error && meetings.length === 0 && (
          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              No transcripts found.
            </Typography>
          </Box>
        )}
        {!loading && !error && meetings.length > 0 && (
          <Table size="small" aria-label="transcripts body">
            <TableBody>
              {meetings.map((meeting) => (
                <TableRow
                  key={meeting.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleRowClick(meeting)}
                >
                  <TableCell style={{ width: "40%" }}>
                    {formatDateTime(meeting.start_time ?? meeting.date)}
                  </TableCell>
                  <TableCell align="left">
                    {meeting.title ?? meeting.name ?? meeting.id}
                  </TableCell>
                  <TableCell style={{ width: "20%" }} align="right">
                    {formatDuration(meeting.duration)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </TableContainer>
  );
};

export default TranscriptTab;
